
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { cn } from '@/lib/utils';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useMapbox } from '@/hooks/map/useMapbox';
import { useRouting, exceedsRideDistance, RouteData } from '@/hooks/map/useRouting';
import { useGeocoding } from '@/hooks/map/useGeocoding';
import { useMapInitialization } from '@/hooks/map/useMapInitialization';
import EtaDisplay from './map/EtaDisplay';
import { AlertCircle, RefreshCw, MapPinOff } from 'lucide-react';

interface MapPreviewProps {
  from: string;
  to: string;
  /**
   * Coordinates for the endpoints, when the caller already has them — the
   * suggestion the rider clicked carried its centre. Supplying them skips a
   * geocoding round-trip per endpoint, and pins the map to the row that was
   * actually chosen rather than to whatever the geocoder makes of the text.
   */
  fromCoords?: [number, number];
  toCoords?: [number, number];
  className?: string;
  showEta?: boolean;
  onRouteCalculated?: (routeData: RouteData) => void;
}

/** The endpoints, resolved to coordinates, plus the route between them. */
interface ResolvedRoute {
  from: [number, number];
  to: [number, number];
  route: RouteData;
}

const MapPreview: React.FC<MapPreviewProps> = ({
  from,
  to,
  fromCoords,
  toCoords,
  className,
  showEta = false,
  onRouteCalculated
}) => {
  const mapContainerId = useRef(`map-${Math.random().toString(36).substring(2, 9)}`).current;
  const {
    mapboxToken,
    isLoading: isTokenLoading,
    error: tokenError,
    refreshToken
  } = useMapbox();
  const { getRoute, addRouteToMap, clearRouteCache } = useRouting();
  const { geocodeLocation } = useGeocoding();

  const [resolved, setResolved] = useState<ResolvedRoute | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isRouting, setIsRouting] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  const {
    map,
    isMapReady,
    error: mapError,
  } = useMapInitialization({
    mapboxToken,
    containerId: mapContainerId,
    // Open on the pickup when we know where it is. The default used to be
    // [-74.5, 40] — the centre of New Jersey, straight out of a Mapbox
    // example — so every rider watched a map of somewhere they were not
    // while their own route was being fetched.
    initialCenter: fromCoords,
  });

  // Function to retry when there's an error
  const handleRetry = useCallback(() => {
    setError(null);
    clearRouteCache(); // Clear cached route so the retry refetches
    setRetryCount(prev => prev + 1); // Increment retry count to trigger useEffect
  }, [clearRouteCache]);

  /**
   * Resolve the route. Deliberately independent of the map.
   *
   * This used to be gated on `isMapReady`, which put four network round-trips
   * in a row on the critical path: the Mapbox token, the map style, two
   * geocodes, then Directions — and the rider was looking at "Loading map…"
   * for the whole chain even though the thing they were waiting for was the
   * fare. Routing runs in parallel with the map now, and skips the geocodes
   * entirely when the caller passed coordinates.
   */
  useEffect(() => {
    // Nothing to route is not the same as routing in progress; leaving the
    // overlay up here would blank the panel for good.
    if (!from || !to) {
      setIsRouting(false);
      return;
    }

    let cancelled = false;
    const processRoute = async () => {
      try {
        setIsRouting(true);
        setError(null);

        const [resolvedFrom, resolvedTo] = await Promise.all([
          fromCoords ?? geocodeLocation(from),
          toCoords ?? geocodeLocation(to),
        ]);
        if (cancelled) return;

        // geocodeLocation answers [0, 0] rather than throwing.
        if (resolvedFrom[0] === 0 && resolvedFrom[1] === 0) {
          throw new Error('Could not find the starting location. Please try a different address.');
        }
        if (resolvedTo[0] === 0 && resolvedTo[1] === 0) {
          throw new Error('Could not find the destination. Please try a different address.');
        }

        // Asked before routing, not after. `getRoute` returns null for every
        // failure, so a distance rejection reached the user as "could not
        // calculate a route" — and MapPreview's own `includes('too long')`
        // branch could never fire. Checking here keeps the real reason, and
        // costs no request or retry for a pair that was never routable.
        const tooFarKm = exceedsRideDistance(resolvedFrom, resolvedTo);
        if (tooFarKm !== null) {
          throw new Error(
            `These locations are about ${Math.round(tooFarKm).toLocaleString()} km apart — too far for a ride. Please choose a closer destination.`,
          );
        }

        const route = await getRoute(resolvedFrom, resolvedTo);
        if (cancelled) return;

        if (!route) {
          throw new Error('Could not calculate a route between these locations');
        }

        setResolved({ from: resolvedFrom, to: resolvedTo, route });
        onRouteCalculated?.(route);
      } catch (err) {
        if (cancelled) return;
        console.error('Error processing route:', err);

        // Keep the specific reason where we have one. The two "could not find
        // the <endpoint>" messages thrown above are the actionable ones — they
        // name which field is wrong — and the old branch discarded both in
        // favour of a generic line that told the rider nothing.
        const message = err instanceof Error ? err.message : '';
        if (message.includes('too far for a ride') || message.includes('too long')) {
          setError(message);
        } else if (message.startsWith('Could not find')) {
          setError(message);
        } else if (message.includes('Could not calculate a route')) {
          setError('No driving route connects these two places. Try a different destination.');
        } else {
          setError('Failed to load route. Please check your locations and try again.');
        }
      } finally {
        if (!cancelled) setIsRouting(false);
      }
    };

    void processRoute();
    return () => {
      cancelled = true;
    };
  }, [
    from,
    to,
    fromCoords,
    toCoords,
    geocodeLocation,
    getRoute,
    onRouteCalculated,
    retryCount, // Add retryCount to dependencies to trigger re-run on retry
  ]);

  // Draw whichever arrives second — the map or the route.
  useEffect(() => {
    if (!isMapReady || !map || !resolved) return;
    addRouteToMap(map, resolved.route, resolved.from, resolved.to);
  }, [isMapReady, map, resolved, addRouteToMap]);

  // If no token is available or loading, show appropriate UI
  if (isTokenLoading) {
    return (
      <div className={cn("bg-muted rounded-md p-4 relative flex flex-col items-center justify-center", className)}>
        <div className="animate-pulse text-muted-foreground">Loading map…</div>
      </div>
    );
  }

  // The Mapbox token is provisioned server-side (the `location` edge function).
  // If it's unavailable we degrade to a non-interactive placeholder rather than
  // asking the user to supply their own token.
  if (tokenError || !mapboxToken) {
    return (
      <div className={cn("bg-muted rounded-md p-4 relative flex flex-col items-center justify-center text-center gap-3", className)}>
        <div className="flex items-center gap-2 text-muted-foreground">
          <MapPinOff className="h-5 w-5" />
          <p className="text-sm">Map preview is unavailable right now.</p>
        </div>
        <button
          onClick={() => refreshToken()}
          className="flex items-center gap-1 px-3 py-1 text-sm text-primary hover:underline"
        >
          <RefreshCw className="h-4 w-4" />
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className={cn("bg-muted rounded-md relative", className)}>
      <div id={mapContainerId} className="absolute inset-0 rounded-md overflow-hidden" />

      {/*
        Only while the route itself is outstanding. The map tiles have their
        own arrival and are visibly a map when they land; blanking the whole
        panel until every request finished is what made a working preview read
        as broken for fifteen seconds.
      */}
      {isRouting && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/50 z-10">
          <div className="animate-pulse text-muted-foreground">Finding your route…</div>
        </div>
      )}

      {(error || mapError) && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/90 z-10">
          <div className="text-center p-4 space-y-4">
            <div className="flex items-center justify-center">
              <AlertCircle className="text-destructive mr-2" />
              <p className="text-destructive">{error || mapError}</p>
            </div>

            <div className="flex gap-2 justify-center">
              <button
                onClick={handleRetry}
                className="flex items-center gap-1 px-3 py-1 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {showEta && resolved && <EtaDisplay routeData={resolved.route} />}
    </div>
  );
};

export default MapPreview;
